/**
 * TouchPointCache - ETag-based smart caching for GeoJSON data
 * Uses conditional requests (If-None-Match) to validate cache freshness
 * Only fetches full data when server confirms it has changed
 */
class TouchPointCache {
    static KEY_PREFIX = 'ttmb_cache_';

    static get(key) {
        try {
            const cached = sessionStorage.getItem(this.KEY_PREFIX + key);
            if (!cached) return null;
            return JSON.parse(cached); // Returns { data, etag }
        } catch (e) {
            return null;
        }
    }

    static set(key, data, etag) {
        try {
            sessionStorage.setItem(this.KEY_PREFIX + key, JSON.stringify({ data, etag }));
        } catch (e) {
            // sessionStorage may be full or unavailable - silently fail
        }
    }

    static remove(key) {
        try {
            sessionStorage.removeItem(this.KEY_PREFIX + key);
        } catch (e) {
            // Ignore errors
        }
    }
}

/**
 * TouchPointBuilder - Builds GeoJSON data for map touch points via Laravel backend
 *
 * Fetches dynamically-built GeoJSON from the Laravel backend endpoint,
 * which reads from the local Products database (already synced from Shopify).
 *
 * Usage:
 *   Primary:   await TouchPointBuilder.primary().build()
 *   Secondary: await TouchPointBuilder.secondary(collectionHandle).build()
 *   Tertiary:  TouchPointBuilder.tertiary(productData).build()
 *   Quinary:   await TouchPointBuilder.quinary().build()
 *
 * @version 2.0.0
 * @date February 2026
 */
class TouchPointBuilder {

    constructor(type, identifier = null) {
        this.type = type;           // 'primary', 'secondary', 'tertiary', 'quinary'
        this.identifier = identifier; // collectionHandle or productData
        this.features = [];
        this.debug = false;
    }

    /**
     * Factory methods for each touch point type
     */
    static primary() {
        return new TouchPointBuilder('primary');
    }

    static secondary(collectionHandle) {
        return new TouchPointBuilder('secondary', collectionHandle);
    }

    static tertiary(productData) {
        return new TouchPointBuilder('tertiary', productData);
    }

    static quinary() {
        return new TouchPointBuilder('quinary');
    }

    /**
     * Main build method - returns Promise<GeoJSON FeatureCollection>
     * ETag-based caching is handled internally by _fetchWithEtag
     */
    async build() {
        try {
            let result;
            switch (this.type) {
                case 'primary':
                    result = await this._buildPrimary();
                    break;
                case 'secondary':
                    result = await this._buildSecondary();
                    break;
                case 'tertiary':
                    result = this._buildTertiary();
                    break;
                case 'quinary':
                    result = await this._buildQuinary();
                    break;
                default:
                    throw new Error(`Unknown touch point type: ${this.type}`);
            }

            return result;
        } catch (error) {
            console.error(`TouchPointBuilder error (${this.type}):`, error);
            return { type: 'FeatureCollection', features: [] };
        }
    }

    /**
     * Primary Touch Point - All trail products
     * Fetches from Laravel backend which builds GeoJSON from local Products DB
     * Uses ETag for conditional requests - only downloads if data changed
     */
    async _buildPrimary() {
        this._log(`Building Primary Touch Point`);
        return await this._fetchWithEtag('/apps/hub/geojson/primary', 'primary');
    }

    /**
     * Secondary Touch Point - Products in a specific collection (by handle)
     * Fetches from Laravel backend which queries products by category
     * Uses ETag for conditional requests - only downloads if data changed
     */
    async _buildSecondary() {
        this._log(`Building Secondary Touch Point for collection: ${this.identifier}`);
        return await this._fetchWithEtag(`/apps/hub/geojson/secondary/${this.identifier}`, `secondary_${this.identifier}`);
    }

    /**
     * Tertiary Touch Point - Single product with start point and car park
     * Uses product data already available on page (no API call needed)
     */
    _buildTertiary() {
        const product = this.identifier;

        this._log(`Building Tertiary Touch Point for product: ${product.title || product.handle}`);

        // Start Point Feature
        if (product.latitude && product.longitude) {
            this.features.push({
                type: 'Feature',
                id: 'feature_primary_' + product.id,
                properties: {
                    marker_type: 'start_point',
                    name: (product.title || 'Trail') + ' - Start Point',
                    description: 'Trail Start Point',
                    product_id: product.id,
                    product_url: product.url || `/products/${product.handle}`
                },
                geometry: {
                    type: 'Point',
                    coordinates: [
                        parseFloat(product.longitude),
                        parseFloat(product.latitude)
                    ]
                }
            });
        }

        // Car Park Feature
        if (product.car_park_latitude && product.car_park_longitude) {
            this.features.push({
                type: 'Feature',
                id: 'feature_secondary_' + product.id,
                properties: {
                    marker_type: 'car_park',
                    name: (product.title || 'Trail') + ' - Car Park',
                    description: 'Car Park',
                    product_id: product.id,
                    product_url: product.url || `/products/${product.handle}`
                },
                geometry: {
                    type: 'Point',
                    coordinates: [
                        parseFloat(product.car_park_longitude),
                        parseFloat(product.car_park_latitude)
                    ]
                }
            });
        }

        this._log(`Tertiary Touch Point complete: ${this.features.length} features`);
        return {
            type: 'FeatureCollection',
            features: this.features
        };
    }

    /**
     * Quinary Touch Point - Stockists map
     * Fetches stockist GeoJSON from Laravel backend via app proxy
     * Uses ETag for conditional requests - only downloads if data changed
     */
    async _buildQuinary() {
        this._log('Building Quinary Touch Point (stockists)');
        return await this._fetchWithEtag('/apps/hub/geojson/quinary', 'quinary');
    }

    /**
     * Fetch with ETag - uses conditional requests for smart caching
     * Sends If-None-Match header, server returns 304 if unchanged
     */
    async _fetchWithEtag(url, cacheKey) {
        const cached = TouchPointCache.get(cacheKey);
        const headers = {};

        // If we have cached data with ETag, send conditional request
        if (cached && cached.etag) {
            headers['If-None-Match'] = cached.etag;
            this._log(`Sending conditional request with ETag: ${cached.etag}`);
        }

        const response = await fetch(url, { headers });

        // 304 Not Modified - data hasn't changed, use cache
        if (response.status === 304 && cached) {
            this._log(`Cache valid (304) for ${cacheKey}`);
            return cached.data;
        }

        if (!response.ok) {
            throw new Error(`GeoJSON API error: ${response.status} ${response.statusText}`);
        }

        // New data - parse and cache with new ETag
        const data = await response.json();
        const newEtag = response.headers.get('ETag');

        if (newEtag) {
            TouchPointCache.set(cacheKey, data, newEtag);
            this._log(`Cached new data with ETag: ${newEtag}`);
        }

        this._log(`Fetched ${data.features?.length || 0} features for ${cacheKey}`);
        return data;
    }

    /**
     * Debug logging helper
     */
    _log(message) {
        if (this.debug) {
            console.log(`[TouchPointBuilder] ${message}`);
        }
    }

    /**
     * Enable debug mode
     */
    enableDebug() {
        this.debug = true;
        return this;
    }
}

// Export for use in mapbox.js.liquid
window.TouchPointBuilder = TouchPointBuilder;
